/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    isEmptyOrNullObject
} from '@msdyn365-commerce/core';
import { getCartState, getCheckoutState, ICartState, ICheckoutState } from '@msdyn365-commerce/global-state';
import { AsynchronousPaymentParameters, CartsDataActions, Cart } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

/**
 * CreateAsynchronousPaymentInput - Input for create AsynchronousPayment.
 */
export class CreateAsynchronousPaymentInput implements IActionInput {
    public paymentParams?: AsynchronousPaymentParameters;

    constructor(paymentParams: AsynchronousPaymentParameters) {
        this.paymentParams = paymentParams;
    }

    /**
     * GetCacheObjectType.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => 'CreateAsynchronousPaymentInput';

    /**
     * Get cache object type.
     * @returns AsynchronousPayment.
     */
    public getCacheObjectType = (): string => `AsynchronousPayment${this.paymentParams?.PaymentAmount ?? ''}`;

    /**
     * Get data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create input method for the GetAsynchronousPayment method.
 * @param inputData - The input data.
 * @returns GetAsynchronousPaymentInput.
 */
export const createAsynchronousPaymentInput = (
    inputData: ICreateActionContext,
    paymentParams: AsynchronousPaymentParameters
): CreateAsynchronousPaymentInput => {
    const { requestContext } = inputData;
    if (requestContext) {
        throw new Error('Unable to get pay by link. Request context is not available.');
    }
    return new CreateAsynchronousPaymentInput(paymentParams);
};

/**
 * Calls the Retail API and returns a cart object based on the passed CreateAsynchronousPaymentInput.
 * @param input - The get asynchronous payment.
 * @param ctx - The action context.
 * @returns Cart.
 */
export async function createAsynchronousPaymentAction(input: CreateAsynchronousPaymentInput, ctx: IActionContext): Promise<Cart> {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Double check.
    if (!input) {
        ctx.telemetry.exception(new Error('[createAsynchronousPaymentAction] No valid Input was provided, failing'));
        throw new Error('[createAsynchronousPaymentAction] No valid Input was provided, failing');
    }

    const { paymentParams } = input;
    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    const cartState: ICartState = await getCartState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    if (
        isEmptyOrNullObject(checkoutState) ||
        isEmptyOrNullObject(checkoutState.checkoutCart) ||
        isEmptyOrNullObject(checkoutState.checkoutCart.cart)
    ) {
        ctx.telemetry.exception(new Error('[createAsynchronousPaymentAction] Unable to get checkoutState cart'));
        throw new Error('[createAsynchronousPaymentAction] Unable to get checkoutState cart');
    }

    if (isEmptyOrNullObject(cartState) || isEmptyOrNullObject(cartState.cart)) {
        ctx.telemetry.exception(new Error('[createAsynchronousPaymentAction] Unable to get cartState cart'));
        throw new Error('[createAsynchronousPaymentAction] Unable to get cartState cart');
    }

    // Cart lines could be updated after copying the cart to checkout state. So need to check cart instead of checkout cart.
    if (!cartState.cart.CartLines || !ArrayExtensions.hasElements(cartState.cart.CartLines)) {
        ctx.telemetry.exception(new Error('[createAsynchronousPaymentAction] Cart is empty'));
        throw new Error('[createAsynchronousPaymentAction] Cart is empty');
    }

    return CartsDataActions.createAsynchronousPaymentAsync(
        { callerContext: ctx },
        checkoutState.checkoutCart.cart.Id,
        paymentParams || {},
        checkoutState.checkoutCart.cart.Version
    )
        .then(cart => {
            if (!cart.AsynchronousPayments || !ArrayExtensions.hasElements(cart.AsynchronousPayments)) {
                throw new Error('[createAsynchronousPaymentAction] Cart does not have any asynchronous payment.');
            }
            return cart;
        })
        .catch(error => {
            ctx.telemetry.exception(error as Error);
            ctx.telemetry.debug('Unable to get Card Asynchronous Payment');
            throw error;
        });
}

/**
 * Get card payment accept point.
 */
export const createAsynchronousPaymentActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/create-asynchronous-payment/get-asynchronous-payment',
    action: createAsynchronousPaymentAction as IAction<Cart>,
    input: createAsynchronousPaymentInput
});

export default createAsynchronousPaymentActionDataAction;
